
import React from 'react';
import { 
  Shield, 
  Target, 
  Zap, 
  Activity, 
  Maximize, 
  Eye, 
  Palette, 
  Lock,
  ChevronRight,
  Sparkles,
  Magnet,
  Trophy,
  Layout,
  Clock,
  Compass,
  Move,
  Wrench,
  Cpu,
  Hexagon
} from 'lucide-react';
import { 
    SimulationConfig, 
    EscapeBreakAnimation, 
    EscapeGapAlignment, 
    EscapeRotationMode, 
    RingCounterPosition,
    EscapeColorMode
} from '@/types';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';
import { EscapeAudioConfig } from './EscapeAudioConfig';
import { CollapsibleSection } from './CollapsibleSection';
import { InfoPopup } from './InfoPopup';

interface EscapeSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  minPlan?: string;
  onUpgradeClick?: () => void;
}

export const EscapeSettingsSection: React.FC<EscapeSettingsSectionProps> = ({ 
    config, setConfig, isLocked, minPlan = "PRO", onUpgradeClick 
}) => {
  const isEscape = config.mode === 'ESCAPE' || config.mode === 'RHYTHMIC_ESCAPE';

  const hudPositions: RingCounterPosition[] = ['CENTER', 'TOP_CENTER', 'BOTTOM_CENTER', 'FOLLOW_BALL', 'NONE'];
  const gapAlignments: EscapeGapAlignment[] = ['ALIGNED', 'RANDOM', 'SPIRAL', 'FIXED_SIZE', 'ONE_SIDE', 'STAGGERED'];
  const rotationModes: EscapeRotationMode[] = ['ALTERNATING', 'CLOCKWISE', 'COUNTER_CLOCKWISE', 'NONE'];
  const ringPatterns: EscapeColorMode[] = ['RAINBOW', 'ALTERNATING', 'GRADIENT', 'UNIFORM', 'INDIVIDUAL'];

  const handleIndividualColorChange = (index: number, color: string) => {
    setConfig(p => {
        const current = Array.isArray(p.escapeIndividualColors) ? p.escapeIndividualColors : [];
        const newColors = [...current];
        newColors[index] = color;
        return { ...p, escapeIndividualColors: newColors };
    });
  };

  const ColorInput = ({ label, value, onChange, tooltip, settingKey, disabled, disabledMessage, modes }: { label: string, value: string, onChange: (v: string) => void, tooltip?: string, settingKey?: string, disabled?: boolean, disabledMessage?: string, modes?: string[] }) => (
    <ControlGroup label={label} tooltip={tooltip} settingKey={settingKey} disabled={disabled} disabledMessage={disabledMessage} modes={modes} darkMode={config.darkMode}>
        <div className={`flex items-center gap-3 p-1.5 rounded-2xl border ${config.darkMode ? 'bg-black/40 border-white/5' : 'bg-white border-neutral-200'} shadow-inner`}>
            <div className="relative group">
                <div className="w-10 h-10 rounded-full border-2 border-white/10 shadow-lg cursor-pointer transition-transform group-hover:scale-105 active:scale-95" style={{ backgroundColor: value }} />
                <input type="color" value={value} onChange={e => onChange(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
            </div>
            <input 
                type="text" 
                value={value} 
                onChange={e => onChange(e.target.value)} 
                className={`flex-1 px-3 py-2 text-[11px] font-mono font-bold border rounded-xl outline-none transition-all ${
                    config.darkMode 
                    ? 'bg-black/20 border-white/5 text-white focus:border-white/20' 
                    : 'bg-neutral-50 border-neutral-100 text-neutral-800 focus:border-neutral-300'
                }`} 
            />
        </div>
    </ControlGroup>
  );

  return (
    <div className="flex flex-col gap-6">
    <CollapsibleSection 
        title="Escape Mode Settings" 
        icon={Shield} 
        darkMode={config.darkMode} 
        settingKey="escapeSettings"
        defaultOpen
        isLocked={isLocked}
        lockRequiredPlan={minPlan as any}
        onUpgradeClick={onUpgradeClick}
    >
        <div className="flex flex-col gap-10 mt-6">
            {/* 1. Core Structure */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                    <Hexagon className="w-3.5 h-3.5 opacity-20" />
                    <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Core Structure</h4>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <ModifierCard 
                        label="Static Rings" 
                        settingKey="escapeStaticMode"
                        active={config.escapeStaticMode} 
                        onToggle={() => setConfig(p => ({...p, escapeStaticMode: !p.escapeStaticMode}))} 
                        darkMode={config.darkMode}
                        icon={Target}
                    />
                    <ModifierCard 
                        label="Time Trial" 
                        settingKey="escapeTimeChallengeMode"
                        active={config.escapeTimeChallengeMode} 
                        onToggle={() => setConfig(p => ({...p, escapeTimeChallengeMode: !p.escapeTimeChallengeMode}))} 
                        darkMode={config.darkMode}
                        icon={Clock}
                    />
                </div>
                <div className="flex flex-col gap-4 mt-4">
                    <SmartInput label="Number of Rings" settingKey="escapeRingCount" min={1} max={100} value={config.escapeRingCount} onChange={v => setConfig(p => ({...p, escapeRingCount: v}))} darkMode={config.darkMode} />
                    <SmartInput label="Gap Size" settingKey="escapeGapSize" min={5} max={100} value={config.escapeGapSize} onChange={v => setConfig(p => ({...p, escapeGapSize: v}))} darkMode={config.darkMode} />
                </div>
            </div>

            {/* 2. Mechanics */}
            <div className="space-y-4">
                 <div className="flex items-center gap-2 mb-2">
                    <Activity className="w-3.5 h-3.5 opacity-20" />
                    <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Movement & Behavior</h4>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <ModifierCard 
                        label="Ring Spacing" 
                        settingKey="escapeRingSpacing"
                        active={config.escapeRingSpacing > 30}
                        onToggle={() => setConfig(p => ({...p, escapeRingSpacing: p.escapeRingSpacing > 30 ? 20 : 60}))}
                        darkMode={config.darkMode}
                        icon={Move}
                    />
                    <ModifierCard 
                        label="Stop Rotation" 
                        settingKey="escapeStopRotationOnHit"
                        active={config.escapeStopRotationOnHit} 
                        onToggle={() => setConfig(p => ({...p, escapeStopRotationOnHit: !p.escapeStopRotationOnHit}))} 
                        darkMode={config.darkMode}
                        icon={Compass}
                    />
                </div>
                <div className="space-y-4 shadow-sm p-4 rounded-3xl bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 mt-4">
                    <SmartInput label="Ring Speed" settingKey="escapeRotationSpeed" min={0} max={20} step={0.1} value={config.escapeRotationSpeed} onChange={v => setConfig(p => ({...p, escapeRotationSpeed: v}))} darkMode={config.darkMode} />
                    <SmartInput label="Ring Spacing" settingKey="escapeRingSpacing" min={10} max={100} value={config.escapeRingSpacing} onChange={v => setConfig(p => ({...p, escapeRingSpacing: v}))} darkMode={config.darkMode} />
                </div>
            </div>

            {/* 3. Assist Systems */}
            <div className="space-y-4">
                 <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-3.5 h-3.5 opacity-20" />
                    <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Assist Systems</h4>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <ModifierCard 
                        label="Shrink Mode" 
                        settingKey="escapeShrinkMode"
                        active={config.escapeShrinkMode} 
                        onToggle={() => setConfig(p => ({...p, escapeShrinkMode: !p.escapeShrinkMode, escapeDynamicShrink: p.escapeDynamicShrink && !p.escapeShrinkMode}))} 
                        darkMode={config.darkMode}
                        icon={Maximize}
                    />
                    <ModifierCard 
                        label="Minimum Radius" 
                        active={config.escapeMinRadius > 0} 
                        onToggle={() => setConfig(p => ({...p, escapeMinRadius: p.escapeMinRadius > 0 ? 0 : 20}))} 
                        darkMode={config.darkMode}
                        icon={Target}
                    />
                </div>
                {config.escapeShrinkMode && (
                    <div className="space-y-4">
                        <SmartInput label="Base Shrink Rate" settingKey="escapeShrinkRate" min={0} max={10} step={0.1} value={config.escapeShrinkRate} onChange={v => setConfig(p => ({...p, escapeShrinkRate: v}))} darkMode={config.darkMode} />
                        <div className="grid grid-cols-2 gap-3">
                            <ModifierCard 
                                label="Dynamic Scaling" 
                                settingKey="escapeDynamicShrink"
                                active={config.escapeDynamicShrink} 
                                onToggle={() => setConfig(p => ({...p, escapeDynamicShrink: !p.escapeDynamicShrink}))} 
                                darkMode={config.darkMode}
                                icon={Activity}
                            />
                        </div>
                    </div>
                )}
                {config.escapeMinRadius > 0 && (
                    <SmartInput label="Minimum Size" settingKey="escapeMinRadius" min={0} max={200} value={config.escapeMinRadius} onChange={v => setConfig(p => ({...p, escapeMinRadius: v}))} darkMode={config.darkMode} />
                )}
                <div className="grid grid-cols-2 gap-3">
                    <ModifierCard 
                        label="Side Guards" 
                        settingKey="escapeSideGuards"
                        active={config.escapeSideGuards} 
                        onToggle={() => setConfig(p => ({...p, escapeSideGuards: !p.escapeSideGuards}))} 
                        darkMode={config.darkMode}
                        icon={Lock}
                    />
                    <ModifierCard 
                        label="Gap Support" 
                        settingKey="escapeGapSupportEnabled"
                        active={config.escapeGapSupportEnabled} 
                        onToggle={() => setConfig(p => ({...p, escapeGapSupportEnabled: !p.escapeGapSupportEnabled}))} 
                        darkMode={config.darkMode}
                        icon={Magnet}
                    />
                </div>
                {config.escapeGapSupportEnabled && (
                    <SmartInput label="Support Strength" settingKey="escapeGapSupportMinRadius" min={10} max={500} value={config.escapeGapSupportMinRadius} onChange={v => setConfig(p => ({...p, escapeGapSupportMinRadius: v}))} darkMode={config.darkMode} />
                )}
            </div>

            {/* 4. Gap Configuration */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                    <Move className="w-3.5 h-3.5 opacity-20" />
                    <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Gap Configuration</h4>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <ControlGroup label="Pattern" settingKey="escapeGapAlignment" darkMode={config.darkMode}>
                        <select 
                            value={config.escapeGapAlignment} 
                            onChange={e => setConfig(p => ({...p, escapeGapAlignment: e.target.value as EscapeGapAlignment}))} 
                            className={`w-full h-10 px-3 border rounded-xl text-[10px] font-black uppercase tracking-widest outline-none ${config.darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-zinc-200 text-zinc-900'}`}
                        >
                            {gapAlignments.map(a => <option key={a} value={a}>{a.replace('_', ' ')}</option>)}
                        </select>
                    </ControlGroup>

                    <ControlGroup label="Rotation" settingKey="escapeRotationMode" darkMode={config.darkMode}>
                        <select 
                            value={config.escapeRotationMode} 
                            onChange={e => setConfig(p => ({...p, escapeRotationMode: e.target.value as EscapeRotationMode}))} 
                            className={`w-full h-10 px-3 border rounded-xl text-[10px] font-black uppercase tracking-widest outline-none ${config.darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-zinc-200 text-zinc-900'}`}
                        >
                            {rotationModes.map(m => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
                        </select>
                    </ControlGroup>
                </div>
                <div className="grid grid-cols-1 gap-4 mt-4">
                    {config.escapeGapAlignment === 'SPIRAL' && (
                        <SmartInput label="Spiral Tightness" settingKey="escapeSpiralTightness" min={1} max={50} value={config.escapeSpiralTightness || 10} onChange={v => setConfig(p => ({...p, escapeSpiralTightness: v}))} darkMode={config.darkMode} />
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <SmartInput label="Opposite Gaps" settingKey="escapeOppositeGapCount" min={0} max={4} value={config.escapeOppositeGapCount || 0} onChange={v => setConfig(p => ({...p, escapeOppositeGapCount: v}))} darkMode={config.darkMode} />
                        <SmartInput label="Opposite Size" settingKey="escapeOppositeGapSize" min={5} max={100} value={config.escapeOppositeGapSize || 20} onChange={v => setConfig(p => ({...p, escapeOppositeGapSize: v}))} darkMode={config.darkMode} />
                    </div>
                </div>
            </div>

            {/* 5. HUD Display */}
            <div className="space-y-4">
                 <div className="flex items-center gap-2 mb-2">
                    <Layout className="w-3.5 h-3.5 opacity-20" />
                    <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">HUD Display</h4>
                </div>
                <div className="grid grid-cols-1 gap-4">
                    <ControlGroup label="Position" settingKey="ringCounterPosition" darkMode={config.darkMode}>
                        <select 
                            value={config.ringCounterPosition} 
                            onChange={e => setConfig(p => ({...p, ringCounterPosition: e.target.value as RingCounterPosition}))} 
                            className={`w-full h-11 px-4 border rounded-xl text-[10px] font-black uppercase tracking-widest outline-none ${config.darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-zinc-200 text-zinc-900'}`}
                        >
                            {hudPositions.map(pos => <option key={pos} value={pos}>{pos.replace('_', ' ')}</option>)}
                        </select>
                    </ControlGroup>
                    <SmartInput label="Text Scale" settingKey="ringCounterFontSize" min={0.5} max={2} value={config.ringCounterFontSize / 100} onChange={v => setConfig(p => ({...p, ringCounterFontSize: v * 100}))} darkMode={config.darkMode} />
                </div>
            </div>

            {/* 6. Ring Pattern Studio */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                    <Palette className="w-3.5 h-3.5 opacity-20" />
                    <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Ring Pattern Studio</h4>
                </div>
                <div className="flex flex-col gap-4">
                    <ControlGroup label="Pattern Mode" settingKey="escapeColorMode" modes={['ESCAPE']} darkMode={config.darkMode}>
                        <div className={`p-1.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100'}`}>
                            <select value={config.escapeColorMode} onChange={e => setConfig(p => ({...p, escapeColorMode: e.target.value as EscapeColorMode}))} className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}>
                                {ringPatterns.map(m => <option key={m} value={m} className={config.darkMode ? "bg-neutral-900 text-white" : "bg-white text-neutral-900"}>{m}</option>)}
                            </select>
                        </div>
                    </ControlGroup>
                    
                    {config.escapeColorMode === 'RAINBOW' && (
                        <SmartInput 
                            label="Spectral Density" 
                            min={0.1} max={50} step={0.1}
                            value={config.escapeRainbowSpeed || 1.0} 
                            onChange={v => setConfig(p => ({...p, escapeRainbowSpeed: v}))} 
                            darkMode={config.darkMode} 
                            settingKey="escapeRainbowSpeed"
                            modes={['ESCAPE']}
                        />
                    )}
                    
                    {(config.escapeColorMode === 'ALTERNATING' || config.escapeColorMode === 'GRADIENT') && (
                        <div className="grid grid-cols-1 gap-4">
                            <ColorInput label="Prism Start" value={config.escapeColorA || '#ff0000'} onChange={v => setConfig(p => ({...p, escapeColorA: v}))} settingKey="escapeColorA" modes={['ESCAPE']} />
                            <ColorInput label="Prism End" value={config.escapeColorB || '#ffffff'} onChange={v => setConfig(p => ({...p, escapeColorB: v}))} settingKey="escapeColorB" modes={['ESCAPE']} />
                        </div>
                    )}
                    
                    {config.escapeColorMode === 'UNIFORM' && (
                        <ColorInput label="Shell Color" value={config.escapeUniformColor || '#3b82f6'} onChange={v => setConfig(p => ({...p, escapeUniformColor: v}))} settingKey="escapeUniformColor" modes={['ESCAPE']} />
                    )}
                    
                    {config.escapeColorMode === 'INDIVIDUAL' && (
                        <ControlGroup label="Layer Profiles" settingKey="escapeIndividualColors" modes={['ESCAPE']} darkMode={config.darkMode}>
                            <div className="flex flex-col gap-3 w-full">
                                <div className="grid grid-cols-5 gap-2.5">
                                    {Array.from({ length: 10 }).map((_, i) => (
                                        <div key={i} className="relative group">
                                            <div 
                                                className={`w-full h-10 rounded-xl border-2 cursor-pointer shadow-lg transition-transform group-hover:scale-105 ${config.darkMode ? 'border-white/10' : 'border-neutral-300'}`} 
                                                style={{ backgroundColor: (Array.isArray(config.escapeIndividualColors) && config.escapeIndividualColors[i]) || '#3b82f6' }} 
                                            />
                                            <input 
                                                type="color" 
                                                value={(Array.isArray(config.escapeIndividualColors) && config.escapeIndividualColors[i]) || '#3b82f6'} 
                                                onChange={e => handleIndividualColorChange(i, e.target.value)}
                                                className="absolute inset-0 opacity-0 cursor-pointer"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </ControlGroup>
                    )}
                </div>
            </div>

            {/* 7. Advanced Physics */}
            <div className="space-y-4">
                 <div className="flex items-center gap-2 mb-2">
                    <Wrench className="w-3.5 h-3.5 opacity-20" />
                    <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Advanced Physics</h4>
                </div>
                <div className="space-y-4">
                    <SmartInput label="Starting Radius" settingKey="containerRadius" min={10} max={800} value={config.containerRadius} onChange={v => setConfig(p => ({...p, containerRadius: v}))} darkMode={config.darkMode} />
                    <div className="grid grid-cols-2 gap-3">
                        <ModifierCard 
                            label="Outer Borders" 
                            settingKey="escapeShowOuterBorders"
                            active={config.escapeShowOuterBorders} 
                            onToggle={() => setConfig(p => ({...p, escapeShowOuterBorders: !p.escapeShowOuterBorders}))} 
                            darkMode={config.darkMode}
                            icon={Maximize}
                        />
                        <ModifierCard 
                            label="Auto-Off" 
                            settingKey="escapeAutoDisableBordersEnabled"
                            active={config.escapeAutoDisableBordersEnabled} 
                            onToggle={() => setConfig(p => ({...p, escapeAutoDisableBordersEnabled: !p.escapeAutoDisableBordersEnabled}))} 
                            darkMode={config.darkMode}
                            icon={Zap}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <ModifierCard 
                            label="Zoom FX" 
                            settingKey="escapeZoomEffectEnabled"
                            active={config.escapeZoomEffectEnabled} 
                            onToggle={() => setConfig(p => ({...p, escapeZoomEffectEnabled: !p.escapeZoomEffectEnabled}))} 
                            darkMode={config.darkMode}
                            icon={Maximize}
                        />
                        <ModifierCard 
                            label="Acceleration" 
                            settingKey="escapeAccelerationEnabled"
                            active={config.escapeAccelerationEnabled} 
                            onToggle={() => setConfig(p => ({...p, escapeAccelerationEnabled: !p.escapeAccelerationEnabled}))} 
                            darkMode={config.darkMode}
                            icon={Activity}
                        />
                    </div>
                    {config.escapeZoomEffectEnabled && (
                        <div className="grid grid-cols-2 gap-4 p-4 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10">
                            <SmartInput label="Zoom Max" settingKey="escapeZoomOutMax" min={1} max={5} step={0.1} value={config.escapeZoomOutMax || 2.5} onChange={v => setConfig(p => ({...p, escapeZoomOutMax: v}))} darkMode={config.darkMode} />
                            <SmartInput label="Zoom Speed" settingKey="escapeZoomOutSpeed" min={0.01} max={1} step={0.01} value={config.escapeZoomOutSpeed || 0.15} onChange={v => setConfig(p => ({...p, escapeZoomOutSpeed: v}))} darkMode={config.darkMode} />
                        </div>
                    )}
                    {config.escapeAutoDisableBordersEnabled && (
                        <SmartInput label="Bounces to Disable" settingKey="escapeAutoDisableBordersBounces" min={1} max={100} value={config.escapeAutoDisableBordersBounces || 10} onChange={v => setConfig(p => ({...p, escapeAutoDisableBordersBounces: v}))} darkMode={config.darkMode} />
                    )}
                    {config.mode === 'RHYTHMIC_ESCAPE' && (
                        <div className="space-y-6 pt-4 border-t border-zinc-950/5">
                            <div className="flex items-center gap-2 mb-2">
                                <Sparkles className="w-3.5 h-3.5 opacity-20" />
                                <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Rhythmic Shield</h4>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <ModifierCard 
                                    label="Inner Shield" 
                                    active={config.rhythmicEscapeShieldRing} 
                                    onToggle={() => setConfig(p => ({...p, rhythmicEscapeShieldRing: !p.rhythmicEscapeShieldRing}))} 
                                    darkMode={config.darkMode}
                                    icon={Shield}
                                />
                                <ModifierCard 
                                    label="Auto Sync" 
                                    active={config.rhythmicEscapeAutoSync} 
                                    onToggle={() => setConfig(p => ({...p, rhythmicEscapeAutoSync: !p.rhythmicEscapeAutoSync}))} 
                                    darkMode={config.darkMode}
                                    icon={Activity}
                                />
                            </div>
                            {config.rhythmicEscapeShieldRing && (
                                <div className="space-y-4 pt-2">
                                    <SmartInput label="Shield Radius" min={20} max={500} value={config.rhythmicEscapeShieldRingRadius} onChange={v => setConfig(p => ({...p, rhythmicEscapeShieldRingRadius: v}))} darkMode={config.darkMode} />
                                    <div className="grid grid-cols-2 gap-4">
                                        <ControlGroup label="Destroy Mode" darkMode={config.darkMode}>
                                            <select 
                                                value={config.rhythmicEscapeShieldRingDestroyMode} 
                                                onChange={e => setConfig(p => ({...p, rhythmicEscapeShieldRingDestroyMode: e.target.value as any}))} 
                                                className={`w-full h-10 px-3 border rounded-xl text-[10px] font-black uppercase tracking-widest outline-none ${config.darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-zinc-200 text-zinc-900'}`}
                                            >
                                                <option value="BOUNCES">Bounces</option>
                                                <option value="TIME">Time</option>
                                            </select>
                                        </ControlGroup>
                                        <SmartInput 
                                            label={config.rhythmicEscapeShieldRingDestroyMode === 'BOUNCES' ? "Bounces" : "Seconds"} 
                                            min={1} max={100} 
                                            value={config.rhythmicEscapeShieldRingDestroyValue} 
                                            onChange={v => setConfig(p => ({...p, rhythmicEscapeShieldRingDestroyValue: v}))} 
                                            darkMode={config.darkMode} 
                                        />
                                    </div>
                                    <ControlGroup label="Break Animation" darkMode={config.darkMode}>
                                        <div className={`p-1 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-neutral-50 border-neutral-200'}`}>
                                            <select 
                                                value={config.rhythmicEscapeShieldBreakAnimation} 
                                                onChange={e => setConfig(p => ({...p, rhythmicEscapeShieldBreakAnimation: e.target.value}))} 
                                                className={`w-full h-11 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}
                                            >
                                                <optgroup label="Shatter & Pieces" className={config.darkMode ? "bg-neutral-900 text-rose-400" : "bg-rose-50 text-rose-900"}>
                                                    <option value="SHATTER">SHATTER</option>
                                                    <option value="PHYSICS_SHATTER">PHYSICS SHATTER</option>
                                                    <option value="GRAVITY_CRUMBLE">GRAVITY CRUMBLE</option>
                                                    <option value="EXPLOSIVE_SHARDS">EXPLOSIVE SHARDS</option>
                                                    <option value="SHRAPNEL">SHRAPNEL</option>
                                                    <option value="DEBRIS_CLOUD">DEBRIS CLOUD</option>
                                                    <option value="SHARD_STORM">SHARD STORM</option>
                                                    <option value="BOUNCY_CHUNKS">BOUNCY CHUNKS</option>
                                                </optgroup>
                                                <optgroup label="Explosive & Force" className={config.darkMode ? "bg-neutral-900 text-orange-400" : "bg-orange-50 text-orange-900"}>
                                                    <option value="EXPLOSION">EXPLOSION</option>
                                                    <option value="SHOCKWAVE">SHOCKWAVE</option>
                                                    <option value="IMPLODE_EXPLODE">IMPLODE EXPLODE</option>
                                                    <option value="SONIC_BOOM">SONIC BOOM</option>
                                                    <option value="RADIAL_SLICE">RADIAL SLICE</option>
                                                    <option value="VOLCANIC_BURST">VOLCANIC BURST</option>
                                                    <option value="CHAIN_REACTION">CHAIN REACTION</option>
                                                </optgroup>
                                                <optgroup label="Cosmic & Gravity" className={config.darkMode ? "bg-neutral-900 text-blue-400" : "bg-blue-50 text-blue-900"}>
                                                    <option value="SUPERNOVA_FRAG">SUPERNOVA FRAG</option>
                                                    <option value="VORTEX">VORTEX</option>
                                                    <option value="VORTEX_SHATTER">VORTEX SHATTER</option>
                                                    <option value="BLACK_HOLE_EAT">BLACK HOLE EAT</option>
                                                    <option value="GALAXY_SWIRL">GALAXY SWIRL</option>
                                                    <option value="NEBULA">NEBULA</option>
                                                    <option value="NEBULA_DRIFT">NEBULA DRIFT</option>
                                                    <option value="PHANTOM_RINGS">PHANTOM RINGS</option>
                                                    <option value="STARDUST">STARDUST</option>
                                                    <option value="RING_EXPAND">RING EXPAND</option>
                                                </optgroup>
                                                <optgroup label="Energy & Light" className={config.darkMode ? "bg-neutral-900 text-cyan-400" : "bg-cyan-50 text-cyan-900"}>
                                                    <option value="PLASMA_WAVE">PLASMA WAVE</option>
                                                    <option value="LIGHTNING_STRIKE">LIGHTNING STRIKE</option>
                                                    <option value="ENERGY_ORBS">ENERGY ORBS</option>
                                                    <option value="AURORA_BOREALIS">AURORA BOREALIS</option>
                                                    <option value="GLOW_FADE">GLOW FADE</option>
                                                    <option value="QUANTUM_LEAP">QUANTUM LEAP</option>
                                                    <option value="PRISM_SPLIT">PRISM SPLIT</option>
                                                    <option value="RAINBOW_BURST">RAINBOW BURST</option>
                                                </optgroup>
                                                <optgroup label="Abstract & Grid" className={config.darkMode ? "bg-neutral-900 text-emerald-400" : "bg-emerald-50 text-emerald-900"}>
                                                    <option value="GLITCH_SQUARE">GLITCH SQUARE</option>
                                                    <option value="HEXAGON_GRID">HEXAGON GRID</option>
                                                    <option value="DATA_STREAM">DATA STREAM</option>
                                                    <option value="PIXEL_BURST">PIXEL BURST</option>
                                                    <option value="PIXEL_GRAVITY">PIXEL GRAVITY</option>
                                                    <option value="FRACTAL_GROWTH">FRACTAL GROWTH</option>
                                                    <option value="CRYSTAL_GROWTH">CRYSTAL GROWTH</option>
                                                </optgroup>
                                                <optgroup label="VFX & Particles" className={config.darkMode ? "bg-neutral-900 text-purple-400" : "bg-purple-50 text-purple-900"}>
                                                    <option value="FIREWORK">FIREWORK</option>
                                                    <option value="CONFETTI">CONFETTI</option>
                                                    <option value="SMOKE_RING">SMOKE RING</option>
                                                    <option value="RIPPLE_WAVE">RIPPLE WAVE</option>
                                                    <option value="GHOST_ECHO">GHOST ECHO</option>
                                                    <option value="TIME_WARP">TIME WARP</option>
                                                    <option value="METEOR_SHOWER">METEOR SHOWER</option>
                                                    <option value="ASTEROID_FIELD">ASTEROID FIELD</option>
                                                </optgroup>
                                                <option value="NONE">NONE</option>
                                            </select>
                                        </div>
                                    </ControlGroup>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {/* 8. Destruction & Visual FX */}
            <div className="space-y-4">
                 <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-3.5 h-3.5 opacity-20" />
                    <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-40 text-rose-500">Destruction & Visual FX</h4>
                </div>
                
                <div className="flex flex-col gap-5 p-5 rounded-[2rem] border bg-rose-500/5 border-rose-500/20 shadow-xl shadow-rose-500/5">
                    <div className="grid grid-cols-1 gap-5">
                        <ControlGroup label="Break Animation" settingKey="escapeBreakAnimation" darkMode={config.darkMode}>
                            <div className={`p-1.5 rounded-2xl border transition-all ${config.darkMode ? 'bg-black/40 border-white/5' : 'bg-white border-neutral-200'} shadow-inner`}>
                                <select 
                                    value={config.escapeBreakAnimation || 'SHATTER'} 
                                    onChange={e => setConfig(p => ({...p, escapeBreakAnimation: e.target.value}))} 
                                    className={`w-full px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}
                                >
                                    <optgroup label="Shatter & Pieces" className={config.darkMode ? "bg-neutral-900 text-rose-400" : "bg-rose-50 text-rose-900"}>
                                        <option value="SHATTER">SHATTER</option>
                                        <option value="PHYSICS_SHATTER">PHYSICS SHATTER</option>
                                        <option value="GRAVITY_CRUMBLE">GRAVITY CRUMBLE</option>
                                        <option value="EXPLOSIVE_SHARDS">EXPLOSIVE SHARDS</option>
                                        <option value="SHRAPNEL">SHRAPNEL</option>
                                        <option value="DEBRIS_CLOUD">DEBRIS CLOUD</option>
                                        <option value="SHARD_STORM">SHARD STORM</option>
                                        <option value="BOUNCY_CHUNKS">BOUNCY CHUNKS</option>
                                    </optgroup>
                                    <optgroup label="Explosive & Force" className={config.darkMode ? "bg-neutral-900 text-orange-400" : "bg-orange-50 text-orange-900"}>
                                        <option value="EXPLOSION">EXPLOSION</option>
                                        <option value="SHOCKWAVE">SHOCKWAVE</option>
                                        <option value="IMPLODE_EXPLODE">IMPLODE EXPLODE</option>
                                        <option value="SONIC_BOOM">SONIC BOOM</option>
                                        <option value="RADIAL_SLICE">RADIAL SLICE</option>
                                        <option value="VOLCANIC_BURST">VOLCANIC BURST</option>
                                        <option value="CHAIN_REACTION">CHAIN REACTION</option>
                                    </optgroup>
                                    <optgroup label="Cosmic & Gravity" className={config.darkMode ? "bg-neutral-900 text-blue-400" : "bg-blue-50 text-blue-900"}>
                                        <option value="SUPERNOVA_FRAG">SUPERNOVA FRAG</option>
                                        <option value="VORTEX">VORTEX</option>
                                        <option value="VORTEX_SHATTER">VORTEX SHATTER</option>
                                        <option value="BLACK_HOLE_EAT">BLACK HOLE EAT</option>
                                        <option value="GALAXY_SWIRL">GALAXY SWIRL</option>
                                        <option value="NEBULA">NEBULA</option>
                                        <option value="NEBULA_DRIFT">NEBULA DRIFT</option>
                                        <option value="PHANTOM_RINGS">PHANTOM RINGS</option>
                                        <option value="STARDUST">STARDUST</option>
                                        <option value="RING_EXPAND">RING EXPAND</option>
                                    </optgroup>
                                    <optgroup label="Energy & Light" className={config.darkMode ? "bg-neutral-900 text-cyan-400" : "bg-cyan-50 text-cyan-900"}>
                                        <option value="PLASMA_WAVE">PLASMA WAVE</option>
                                        <option value="LIGHTNING_STRIKE">LIGHTNING STRIKE</option>
                                        <option value="ENERGY_ORBS">ENERGY ORBS</option>
                                        <option value="AURORA_BOREALIS">AURORA BOREALIS</option>
                                        <option value="GLOW_FADE">GLOW FADE</option>
                                        <option value="QUANTUM_LEAP">QUANTUM LEAP</option>
                                        <option value="PRISM_SPLIT">PRISM SPLIT</option>
                                        <option value="RAINBOW_BURST">RAINBOW BURST</option>
                                    </optgroup>
                                    <optgroup label="Abstract & Grid" className={config.darkMode ? "bg-neutral-900 text-emerald-400" : "bg-emerald-50 text-emerald-900"}>
                                        <option value="GLITCH_SQUARE">GLITCH SQUARE</option>
                                        <option value="HEXAGON_GRID">HEXAGON GRID</option>
                                        <option value="DATA_STREAM">DATA STREAM</option>
                                        <option value="PIXEL_BURST">PIXEL BURST</option>
                                        <option value="PIXEL_GRAVITY">PIXEL GRAVITY</option>
                                        <option value="FRACTAL_GROWTH">FRACTAL GROWTH</option>
                                        <option value="CRYSTAL_GROWTH">CRYSTAL GROWTH</option>
                                    </optgroup>
                                    <optgroup label="VFX & Particles" className={config.darkMode ? "bg-neutral-900 text-purple-400" : "bg-purple-50 text-purple-900"}>
                                        <option value="FIREWORK">FIREWORK</option>
                                        <option value="CONFETTI">CONFETTI</option>
                                        <option value="SMOKE_RING">SMOKE RING</option>
                                        <option value="RIPPLE_WAVE">RIPPLE WAVE</option>
                                        <option value="GHOST_ECHO">GHOST ECHO</option>
                                        <option value="TIME_WARP">TIME WARP</option>
                                        <option value="METEOR_SHOWER">METEOR SHOWER</option>
                                        <option value="ASTEROID_FIELD">ASTEROID FIELD</option>
                                    </optgroup>
                                    <option value="NONE">NONE</option>
                                </select>
                            </div>
                        </ControlGroup>

                        <SmartInput label="Blast Duration" settingKey="blastDuration" min={200} max={3000} value={config.blastDuration} onChange={v => setConfig(p => ({...p, blastDuration: v}))} darkMode={config.darkMode} />
                        
                        <div className="grid grid-cols-2 gap-4">
                            <SmartInput label="Shatter Force" settingKey="shatterForce" min={1} max={50} step={0.1} value={config.shatterForce || 15} onChange={v => setConfig(p => ({...p, shatterForce: v}))} darkMode={config.darkMode} />
                            <SmartInput label="Shatter Gravity" settingKey="shatterGravity" min={0} max={2} step={0.05} value={config.shatterGravity || 0.6} onChange={v => setConfig(p => ({...p, shatterGravity: v}))} darkMode={config.darkMode} />
                        </div>

                        <ColorInput label="Blast Color" value={config.blastColor || '#ffffff'} onChange={v => setConfig(p => ({...p, blastColor: v}))} settingKey="blastColor" />
                        
                        <ModifierCard 
                            label="Match Ring Color" 
                            settingKey="blastMatchRingColor"
                            active={config.blastMatchRingColor} 
                            onToggle={() => setConfig(p => ({...p, blastMatchRingColor: !p.blastMatchRingColor}))} 
                            darkMode={config.darkMode}
                            icon={Palette}
                        />
                    </div>
                </div>

                <div className="mt-8 border-t border-zinc-500/10 pt-8">
                    <EscapeAudioConfig config={config} setConfig={setConfig} />
                </div>
            </div>
        </div>
    </CollapsibleSection>
    </div>
  );
};

const ModifierCard = ({ label, active, onToggle, darkMode, icon: Icon, settingKey }: any) => (
    <div className={`relative flex items-center gap-3 p-3 rounded-2xl border transition-all duration-300 group flex-1 ${active ? (darkMode ? 'bg-white/10 border-white/10 text-white' : 'bg-white border-zinc-950/10 text-zinc-900 shadow-sm') : 'bg-transparent border-transparent text-zinc-400'}`}>
        <button className="absolute inset-0 w-full h-full cursor-pointer z-0" onClick={onToggle} />
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all pointer-events-none z-10 ${active ? 'bg-neon-cyan text-white shadow-lg shadow-cyan-500/20' : 'bg-zinc-200 text-zinc-500 group-hover:bg-zinc-300'}`}>
            <Icon className="w-4 h-4" />
        </div>
        <div className="flex flex-col items-start pr-1 pointer-events-none z-10">
            <span className="text-[9px] font-black uppercase tracking-tight leading-none whitespace-nowrap">{label}</span>
            <span className={`text-[7px] font-bold uppercase mt-1 ${active ? 'text-cyan-500' : 'text-zinc-400'}`}>{active ? 'Active' : 'Off'}</span>
        </div>
        {settingKey && <div className="absolute right-2 top-2 z-20"><InfoPopup content="" settingKey={settingKey} darkMode={darkMode} /></div>}
    </div>
);
